import {Dispatch, memo, useCallback} from 'react';

import styled from 'styled-components';

import {media} from '~/shared/theme/media';
import {getLocalizationService} from '~/shared/services/localisationService';
import {AddressFinderVariant} from '~/shared/consts/addressConsts';
import {flexColumn} from '~/shared/theme/FlexLayout';
import useHomePageAddressSearch from '~/common/hooks/address/useHomePageAddressSearch';
import {AddressFinderLocation} from '~/shared/services/analytics/analyticsModels';
import {body14Bold, bodyRegular} from '~/shared/theme/typography';

import ImageWithAlt from '../ImageWithAlt';
import AddressFinder from '../AddressFinder';

import illustrationURL from './assets/address-finder-illustration.png';

const Root = styled.div`
  background-color: ${({theme}) => theme.colors.surface};
  padding-top: 32px;
  gap: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-right: 16px;
  padding-left: 16px;
  font-size: 16px;
  ${media.minLargeMobile`
    padding-right: 40px;
    padding-left: 40px;
    padding-bottom: 44px;
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
  `}
`;

const Title = styled.p`
  ${bodyRegular}
  text-align: center;
`;

const StyledIllustration = styled(ImageWithAlt)`
  width: 180px;
  height: 180px;
`;

const AddressWrapper = styled.div`
  ${flexColumn}
  width: 100%;
  justify-content: flex-start;
  gap: 8px;
`;

const H3 = styled.h3`
  ${body14Bold}
  color: ${({theme}) => theme.colors.contentDefault};
`;

const RegisterAddressFinder = ({setLoading}: {
  setLoading: Dispatch<boolean>;
}) => {
  const {t} = getLocalizationService();

  const onChangeAddressRequestStart = useCallback(() => {
    setLoading(true);
  }, [setLoading]);

  const onChangeAddressRequestFinish = useCallback(() => {
    setLoading(false);
  }, [setLoading]);
  
  const {
    disableSubmit,
    setDisableSubmit,
    onAddressFinderChange,
    onNewAddressSubmit,
  } = useHomePageAddressSearch({onChangeAddressRequestFinish, onChangeAddressRequestStart});

  return (
    <Root data-test-id="register-address-finder-step">
      <Title>{t('we_need_your_location_to_get_this_order_cooking')}</Title>
      <StyledIllustration src={illustrationURL} alt={t('we_need_your_location_to_get_this_order_cooking')} />
      <AddressWrapper>
        <H3>{t('delivery_address')}</H3>
        <AddressFinder
          variant={AddressFinderVariant.PIE}
          location={AddressFinderLocation.REGISTRATION}
          placeholder={t('street_house_number_city')}
          disableAutoFocus
          onChange={onAddressFinderChange}
          submit={onNewAddressSubmit}
          disableSubmit={disableSubmit}
          setDisableSubmit={setDisableSubmit}
          isAddAddressMode
        />
      </AddressWrapper>
    </Root>
  );
};

export default memo(RegisterAddressFinder);
